import { Attachment, Drawer, Text } from '@billy/design-system'
import { useTranslation } from 'react-i18next'

import { useUpdateAttachment } from '@features/attachments/hooks/useUpdateAttachment'
import { useDeleteDaybookTransactionAttachment } from '@features/daybook/hooks/useDeleteDaybookTransactionAttachment'
import { useInvalidateDaybookTransaction } from '@features/daybook/hooks/useInvalidateDaybookTransaction'
import { useUploadDaybookTransactionAttachments } from '@features/daybook/hooks/useUploadDaybookTransactionAttachments'
import { DaybookTransaction } from '@features/daybook/types/daybookTransaction'
import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { useInvalidateTransactions } from '@features/transactions/hooks/useInvalidateTransactions'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { Scope } from '../../../../../../../../enums/scope'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useTransactionStaticActions } from '../../../../hooks/useTransactionStaticActions'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import { AttachmentsSection } from '../../elements/AttachmentsSection'
import { BookingsSection } from '../../elements/BookingsSection/BookingsSection'
import { DetailsSection } from '../../elements/DetailsSection'
import { DrawerSection } from '../../elements/DrawerSection'
import { HeroSection } from '../../elements/HeroSection'
import { Separator } from '../../elements/Separator'

interface DaybookTransactionViewProps {
  daybookTransaction: DaybookTransaction
}

export const DaybookTransactionView = ({ daybookTransaction }: DaybookTransactionViewProps) => {
  const { t } = useTranslation()
  const { isVoidedEntry, transactionId, transactionNo, originatorCurrencyId } = useTransactionDrawerContext()
  const { organization } = useUserOrganization()

  const { attachments, createdTime, description, entryDate, extendedDescription, id, voucherNo } = daybookTransaction

  const { invalidateDaybookTransaction } = useInvalidateDaybookTransaction()
  const { invalidateTransactions } = useInvalidateTransactions()
  const { update, isLoading: isLinking } = useUpdateAttachment()
  const { handleFilesDropped, isUploading } = useUploadDaybookTransactionAttachments({ daybookTransactionId: id })
  const { deleteAttachment } = useDeleteDaybookTransactionAttachment(id)
  const { handleSelect, items, modals } = useTransactionStaticActions({
    isVoided: isVoidedEntry,
    originatorId: id,
    originatorType: TransactionOriginatorType.DaybookTransaction,
    transactionId,
  })

  const handleAttachmentSelect = (attachment: Attachment) => {
    update(
      { id: attachment.id, ownerReference: `daybookTransaction:${id}`, ownerId: id },
      {
        onSuccess: () => {
          invalidateDaybookTransaction(id)
          invalidateTransactions()
        },
      },
    )
  }

  const sourceLabel = voucherNo && `${t('ledger.details.receipt_no')} ${voucherNo || ''}`
  const descriptionText = [description, extendedDescription].filter(Boolean).join(': ')

  return (
    <>
      <Drawer.Header
        actions={items}
        closable
        onAction={handleSelect}
        title={t('ledger.drawer_title', { number: transactionNo })}
      />
      <Drawer.Body withPadding={false} gap="0">
        <HeroSection amount={null} currency={originatorCurrencyId} entryDate={entryDate} source={sourceLabel} />
        <Separator />
        <BookingsSection originatorCurrency={organization?.baseCurrencyId} withTotal={false} />
        <Separator />
        {descriptionText && (
          <>
            <DrawerSection title={t('ledger.description')}>
              <Text>{descriptionText}</Text>
            </DrawerSection>
            <Separator />
          </>
        )}
        <DetailsSection createdTime={createdTime} number={voucherNo} />
        <Separator />
        <AttachmentsSection
          attachments={attachments}
          isLoading={isUploading || isLinking}
          isVoided={isVoidedEntry}
          onAttachmentSelect={handleAttachmentSelect}
          onFilesDropped={handleFilesDropped}
          onRemove={deleteAttachment}
          scope={Scope.DaybookWrite}
          trackingContext={TrackingContext.DaybookAttachment}
        />
      </Drawer.Body>
      {modals}
    </>
  )
}
